import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getHostMode,
  buildCrossDomainUrl,
  modeForPath,
  isSplitDomain,
} from '../lib/hostRouter';

const TITLE_BY_MODE = {
  consumer: 'SignVoice — Sign Language to Voice',
  talentconnect: 'Talent Connect — Inclusive Hiring Platform',
};

const META_DESC_BY_MODE = {
  consumer: 'SignVoice translates American Sign Language to spoken voice in real time, directly in your browser. Accessible communication for everyone.',
  talentconnect: 'Talent Connect — pre-qualify ASL candidates in seconds with QR-driven, sign-language-first interviews.',
};

function setMeta(name, content) {
  if (typeof document === 'undefined' || !content) return;
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function RedirectingSplash() {
  return (
    <div
      data-testid="hostguard-redirecting"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0A0F25',
        color: '#F8FAFC',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 360, padding: '0 24px' }}>
        <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.5, marginBottom: 12 }}>
          Switching surface
        </p>
        <p style={{ fontSize: 18, fontWeight: 600 }}>Redirecting…</p>
      </div>
    </div>
  );
}

/**
 * HostGuard — enforces surface boundaries between consumer + Talent Connect
 * sub-domains. Dormant by default (unified deploy). Activated ONLY when
 * `isSplitDomain()` returns true — that is, when BOTH env vars
 * REACT_APP_CONSUMER_HOST and REACT_APP_TALENTCONNECT_HOST are set to
 * DIFFERENT hostnames. See lib/hostRouter.js for the topology contract.
 *
 * Behaviour (split-domain deploy only):
 *   - consumer host + path that belongs to talentconnect → cross-origin redirect
 *   - talentconnect host + path that belongs to consumer  → cross-origin redirect
 *   - shared paths (/privacy, /terms) are served on both surfaces
 *   - dev mode / unified deploy → no redirects (no-op passthrough)
 *
 * The redirect URL is computed during render so we can return the splash
 * directly without triggering set-state-in-effect.
 */
export default function HostGuard({ children }) {
  const location = useLocation();
  // hostMode is locked to mount-time — re-evaluating on every render would
  // re-detect the same hostname anyway.
  const [hostMode] = useState(getHostMode);
  const [splitDomain] = useState(isSplitDomain);

  const redirectUrl = useMemo(() => {
    if (!splitDomain || hostMode === 'dev') return null;
    const target = modeForPath(location.pathname);
    if (target === null || target === hostMode) return null;
    const url = buildCrossDomainUrl(
      target,
      location.pathname + location.search + location.hash,
    );
    return url.startsWith('http') ? url : null;
  }, [splitDomain, hostMode, location.pathname, location.search, location.hash]);

  // Side effect 1 — set title/meta once per host mode (only meaningful in
  // split-domain; unified deploy lets each page own its title).
  useEffect(() => {
    if (!splitDomain || hostMode === 'dev') return;
    const title = TITLE_BY_MODE[hostMode];
    if (title) document.title = title;
    setMeta('description', META_DESC_BY_MODE[hostMode]);
  }, [splitDomain, hostMode]);

  // Side effect 2 — actually navigate to the cross-domain URL when computed.
  useEffect(() => {
    if (redirectUrl) window.location.assign(redirectUrl);
  }, [redirectUrl]);

  if (redirectUrl) return <RedirectingSplash />;
  return children;
}
